import { useState, useMemo, useCallback, useEffect } from "react";
import { JobFinancialsPanel } from "@/components/JobFinancialsPanel";
import { JobSpecViewer } from "@/components/jobspec/JobSpecViewer";
import { JobSpecExecutionPanel } from "@/components/jobspec/JobSpecExecutionPanel";
import { JobSpecQAPanel } from "@/components/jobspec/JobSpecQAPanel";
import { SpecReconciliationGate } from "@/components/jobspec/SpecReconciliationGate";
import { MaterialKitPanel } from "@/components/jobspec/MaterialKitPanel";
import { JobBomPanel } from "@/components/jobspec/JobBomPanel";
import { SpecOutcomeCard } from "@/components/jobspec/SpecOutcomeCard";
import { JobEconomicsBreakdown } from "@/components/JobEconomicsBreakdown";
import { getStageConfig, getOrderedStages } from "@/data/workflowEngine";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CommunicationTimeline } from "@/components/CommunicationTimeline";
import { AutomationControlPanel } from "@/components/AutomationControlPanel";
import { buildCommunicationTimeline, getJobSummary } from "@/data/communicationEngine";
import { buildLineageFromRecords } from "@/data/versioningSystem";
import { getDispatchRecommendations, getPerformanceLabel, getPerformanceBadgeClasses, type DispatchOverride } from "@/data/dispatchRecommendation";
import { isInRescission, getRescissionDaysRemaining, formatRescissionEnd } from "@/data/rescission";
import { getJobBlockers } from "@/data/workflowBlockers";
import { getJobBlockersUnified } from "@/data/unifiedBlockers";
import { getJobDisplayValue } from "@/data/jobPricingSelectors";
import { getJobNextSteps } from "@/data/nextStepEngine";
import { getJobNextAction } from "@/data/nextActionEngine";
import { RecordHealthPanel } from "@/components/RecordHealthPanel";
import { JobWarehouseExecution } from "@/components/WarehouseExecution";
import { ContractDocumentView } from "@/components/ContractDocumentView";
import { ChangeOrderDialog } from "@/components/ChangeOrderDialog";
import { ChangeOrderHistory } from "@/components/ChangeOrderHistory";
import { VersionTimeline, VersionComparisonBanner } from "@/components/VersionTimeline";
import type { ChangeOrder } from "@/data/changeOrderTypes";
import { useSchedulingConflicts } from "@/hooks/useSchedulingConflicts";

import { getJobHealth } from "@/data/jobHealth";
import { getJobSuggestedActions, getLastActivity, formatRelativeTime, type SuggestedAction } from "@/data/suggestedActions";
import { useAppData } from "@/data/AppDataContext";
import type { CustomerCredit, Job } from "@/data/types";
import { OrderingModal } from "@/components/OrderingModal";
import { OrderPrepCard } from "@/components/OrderPrepCard";
import { RescheduleModal } from "@/components/quick-actions/RescheduleModal";
import { QuickAssignModal } from "@/components/quick-actions/QuickAssignModal";
import { QuickResolveIssueModal } from "@/components/quick-actions/QuickResolveIssueModal";
import { runAction, type ExecutableAction } from "@/lib/actionRunner";
import { JobService } from "@/services/job.service";
// suggestNextStep removed — push modals now render PushNextStepPanel inline,
// so a duplicate toast was a conflicting signal. Chain continuity goes through
// each modal's onChooseNextStep callback below.
import { toast } from "sonner";
import { buildJobDocuments, buildJobAuditTrail } from "@/data/documentTypes";
import { DocumentCenter } from "@/components/DocumentCenter";
import { AuditTrailTimeline } from "@/components/AuditTrailTimeline";
import { generateAlerts, getJobAlerts } from "@/data/alertsEngine";
import { AlertsPanel } from "@/components/AlertsPanel";
import { getJobPriority, getJobFinancialSummary } from "@/data/jobPriority";
import { getFinancialSummary as getCollectionSummary } from "@/data/invoiceEngine";
import { useRole } from "@/data/RoleContext";
import { usePermissions } from "@/data/PermissionsContext";
import { PermissionGate } from "@/components/PermissionGate";
import { useFinancialPermissions } from "@/hooks/useFinancialPermissions";
import { computeScorecard } from "@/hooks/useContractorScorecards";
import { ReliabilityBadge } from "@/components/ReliabilityBadge";
import { CreateCreditForm } from "@/components/CreateCreditForm";
import { StorageThumbnailImage } from "@/components/media/StorageThumbnailImage";
import type { ProjectConfiguration } from "@/components/ProjectConfigurator";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { SendOpportunityDialog } from "@/components/SendOpportunityDialog";
import { SelectWinnerDialog } from "@/components/SelectWinnerDialog";
import { ActivityTimeline } from "@/components/ActivityTimeline";
import { UnifiedJobTimeline } from "@/components/UnifiedJobTimeline";
import { Progress } from "@/components/ui/progress";
import { EditMarketingModal } from "@/components/EditMarketingModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ArrowLeft, Phone, Mail, MapPin, User, Calendar, AlertTriangle, DollarSign,
  CheckCircle, XCircle, Clock, Send, Shield, Package, Truck, ClipboardCheck,
  Camera, FileText, ShieldAlert, OctagonAlert, Users, Image, Trophy,
  Wrench, ImagePlus, FileCheck, ExternalLink,
  Boxes, Megaphone, CreditCard, ArrowRight, Circle, Zap, ClipboardList,
  Link2, Pencil,
} from "lucide-react";
import { LifecycleTracker } from "@/components/LifecycleTracker";
import { formatAddress } from "@/lib/addressUtils";
import { isGrowthChannelSource, getGrowthChannelLabel } from "@/lib/growthChannel";
import { suggestChannel, getChannelById } from "@/lib/growthChannelMapping";
import { getAttachments, createSignedAttachmentUrl } from "@/services/attachment.service";

/* ── Shared sub-components ── */

function SectionHeader({ icon: Icon, label, action }: { icon: React.ElementType; label: string; action?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">{label}</h2>
      </div>
      {action}
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-xs font-medium text-right max-w-[60%]">{value}</span>
    </div>
  );
}

function formatDispatchTimeSlot(
  timeSlot?: string,
  scheduledStartTime?: string,
  scheduledEndTime?: string,
): string {
  const explicit = String(timeSlot ?? "").trim();
  if (explicit) return explicit;
  if (!scheduledStartTime) return "Not set";

  const start = new Date(scheduledStartTime);
  if (Number.isNaN(start.getTime())) return "Not set";
  const startLabel = start.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (scheduledEndTime) {
    const end = new Date(scheduledEndTime);
    if (!Number.isNaN(end.getTime())) {
      const endLabel = end.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
      return `${startLabel} - ${endLabel}`;
    }
  }

  return startLabel;
}

function isRenderablePhoto(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const v = value.trim();
  if (!v) return false;
  if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:") || v.startsWith("blob:") || v.startsWith("/")) return true;
  return /\.(png|jpe?g|webp|gif|bmp|svg)$/i.test(v);
}

function collectJobPhotoUrls(job: Job, issues: { photo: string }[]): string[] {
  const merged = [
    ...(Array.isArray(job.photos) ? job.photos : []),
    ...issues.map((i) => i.photo),
  ].filter(isRenderablePhoto);

  const seen = new Set<string>();
  return merged.filter((url) => {
    if (seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

function dedupePhotoUrls(urls: string[]): string[] {
  const seen = new Set<string>();
  return urls.filter((url) => {
    if (!isRenderablePhoto(url) || seen.has(url)) return false;
    seen.add(url);
    return true;
  });
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function displayIdentity(value: unknown, fallback: string): string {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return fallback;
  return looksLikeUuid(text) ? fallback : text;
}

/* ── Progress helpers ── */
function computeJobProgress(job: any, openIssues: any[]) {
  const milestones = [
    { label: "Permit", done: !job.permitRequired || job.permitStatus === "Approved" },
    { label: "Materials", done: ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness) },
    { label: "Crew", done: job.assignmentType !== "Unassigned" },
    { label: "QA", done: job.qaSubmitted && openIssues.length === 0 },
  ];
  const doneCount = milestones.filter(m => m.done).length;
  const pct = Math.round((doneCount / milestones.length) * 100);
  return { milestones, pct, doneCount };
}

function getProgressColor(pct: number, hasBlocker: boolean) {
  if (hasBlocker) return "bg-destructive";
  if (pct >= 100) return "bg-success";
  if (pct >= 50) return "bg-warning";
  return "bg-destructive";
}

function getProgressLabel(pct: number, hasBlocker: boolean) {
  if (hasBlocker) return { text: "Blocked", color: "text-destructive" };
  if (pct >= 100) return { text: "Ready", color: "text-success" };
  if (pct >= 50) return { text: "In Progress", color: "text-warning" };
  if (pct > 0) return { text: "At Risk", color: "text-destructive" };
  return { text: "Not Started", color: "text-muted-foreground" };
}

/* ── Main Component ── */

export default function JobDetail() {
  const { id } = useParams<{ id: string }>();
  const { jobs, setJobs, issues, opportunities, contractors, credits, setCredits, quotes, installerWrapRecords, conversations, messages, appointmentResults } = useAppData();
  const [quotePhotoUrls, setQuotePhotoUrls] = useState<string[]>([]);
  const { role } = useRole();
  const { hasPermission: hasPerm } = usePermissions();
  const fin$ = useFinancialPermissions();
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [selectWinnerOpen, setSelectWinnerOpen] = useState(false);
  const [showJobCreditForm, setShowJobCreditForm] = useState(false);
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [changeOrderOpen, setChangeOrderOpen] = useState(false);
  const [issuesModalOpen, setIssuesModalOpen] = useState(false);
  // Modal-first quick action state — mirrors JobsList pattern so CTAs execute in place
  const [orderingOpen, setOrderingOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [resolveIssueTarget, setResolveIssueTarget] = useState<import("@/data/types").Issue | null>(null);
  const [editMarketingOpen, setEditMarketingOpen] = useState(false);
  const navigate = useNavigate();
  const { getJobConflicts } = useSchedulingConflicts(jobs);

  const job = jobs.find((j) => j.id === id);

  // Most recent appointment result for this customer — drives the
  // "Appointment outcome" deep-link in the header. Customer-name match
  // mirrors how Jobs ↔ Customer Profile are linked elsewhere.
  const linkedAppointmentResult = useMemo(() => {
    if (!job) return null;
    const matches = appointmentResults.filter((r) => r.customerName === job.customerName);
    if (matches.length === 0) return null;
    return [...matches].sort((a, b) =>
      new Date(b.appointmentDatetime).getTime() - new Date(a.appointmentDatetime).getTime()
    )[0];
  }, [appointmentResults, job]);

  const dispatchRec = useMemo(() => {
    if (!job) return { jobReady: false, readinessStatus: "Not Ready", recommendations: [], topPick: null, blocked: true };
    return getDispatchRecommendations(job, contractors, opportunities, jobs, issues, credits);
  }, [job, contractors, opportunities, jobs, issues, credits]);

  const jobIssues = job ? issues.filter((i) => i.jobId === id) : [];
  const openIssues = jobIssues.filter((i) => i.status === "Open" || i.status === "In Progress");
  const resolvedIssues = jobIssues.filter((i) => i.status === "Resolved" || i.status === "Closed");

  /** Modal-first dispatcher — same pattern as JobsList. Routes SuggestedAction → in-page modal. */
  const handleActionClick = useCallback(
    async (action: SuggestedAction) => {
      if (!job) return;
      let executable: ExecutableAction;
      if (action.executionMode === "modal" && action.icon === "materials") {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setOrderingOpen(true) };
      } else if (action.executionMode === "modal" && action.icon === "schedule") {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setRescheduleOpen(true) };
      } else if (action.executionMode === "modal" && (action.icon === "dispatch" || action.icon === "crew")) {
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setAssignOpen(true) };
      } else if (action.executionMode === "modal" && action.icon === "issue") {
        // Pick highest-severity open issue for quick-resolve; full triage stays as secondary link.
        const sevRank = (s: string) => (s === "Urgent" ? 0 : s === "High" ? 1 : s === "Medium" ? 2 : 3);
        const top = [...openIssues].sort((a, b) => sevRank(a.severity) - sevRank(b.severity))[0] || null;
        executable = { label: action.cta || action.label, executionMode: "modal", openModal: () => setResolveIssueTarget(top) };
      } else {
        executable = { label: action.cta || action.label, executionMode: "navigate", route: action.link || `/jobs/${job.id}` };
      }
      const handled = await runAction(executable, { navigate });
      if (!handled && action.link) navigate(action.link);
    },
    [job, openIssues, navigate],
  );

  /** Persist ordering modal output → job.materialReadiness + activity. */
  const handleOrderingSubmit = useCallback(
    async (data: { jobId: string; vendor: string; orderType: "Batch" | "Direct" | "Rush"; notes: string }) => {
      const target = jobs.find((j) => j.id === data.jobId);
      if (!target) return;
      const patch: Partial<Job> = {
        materialReadiness: "Ordered" as any,
        planningStatus: "Ordered" as const,
        selectedVendor: data.vendor,
        activityHistory: [
          ...target.activityHistory,
          {
            id: `jd-order-${Date.now()}`,
            date: new Date().toISOString(),
            action: `Materials ordered — ${data.vendor} (${data.orderType})`,
            user: "Ops",
            noteType: "ops" as const,
          },
        ],
      };
      const { data: persisted, error } = await JobService.update(target.id, patch);
      if (error || !persisted) {
        console.error("Failed to persist ordering update:", error);
        toast.error("Failed to place order");
        return;
      }
      setJobs((prev) => prev.map((j) => (j.id === persisted.id ? persisted : j)));
      toast.success("Order placed", { description: `${data.vendor} — ${data.orderType}` });
      // NOTE: do NOT close the modal here — OrderingModal renders its inline
      // PushNextStepPanel and the user picks the next step from there.
      // Chain continuation handled by onChooseNextStep below.
    },
    [jobs, setJobs],
  );

  /** Refresh jobs list after marketing edit is saved from modal. */
  const handleMarketingSaved = useCallback((updatedJob: Job) => {
    setJobs((prev) => prev.map((j) => (j.id === updatedJob.id ? updatedJob : j)));
  }, [setJobs]);

  const unifiedBlockers = useMemo(() => {
    if (!job) return [];
    return getJobBlockersUnified(job, openIssues, quotes);
  }, [job, openIssues, quotes]);

  const jobNextSteps = useMemo(() => {
    if (!job) return [];
    return getJobNextSteps(job, openIssues, quotes);
  }, [job, openIssues, quotes]);

  const jobCompleteness = useMemo(() => {
    if (!job) return [];
    const sourceQuote = quotes.find(q => q.convertedJobId === job.id);
    return [
      { label: "Contract Signed", done: !!sourceQuote?.contract?.status && sourceQuote.contract.status === "Signed" },
      { label: "Deposit Collected", done: !!sourceQuote?.deposit },
      { label: "Rescission Cleared", done: job.rescissionStatus !== "In Rescission" },
      { label: "Permit", done: !job.permitRequired || job.permitStatus === "Approved" },
      { label: "Materials Ready", done: ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness) },
      { label: "Crew Assigned", done: job.assignmentType !== "Unassigned" },
      { label: "Install Scheduled", done: !!job.scheduledInstallDate },
      { label: "QA Submitted", done: job.qaSubmitted },
      { label: "Issues Resolved", done: openIssues.length === 0 },
    ];
  }, [job, openIssues, quotes]);

  const linkedQuoteId = useMemo(() => {
    if (!job) return null;
    const src = quotes.find((q) => q.convertedJobId === job.id || q.linkedJobId === job.id);
    return src?.id ?? null;
  }, [quotes, job]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!linkedQuoteId) {
        if (!cancelled) setQuotePhotoUrls([]);
        return;
      }
      try {
        const records = await getAttachments("quote", linkedQuoteId);
        const photoRecords = records.filter((r: any) => r.attachment_type === "photo" || r.attachmentType === "photo");
        const urls = await Promise.all(
          photoRecords.map(async (r: any) => {
            const path = String(r.file_path ?? r.filePath ?? "").trim();
            if (!path) return "";
            return createSignedAttachmentUrl(path, "job-documents").catch(() => "");
          }),
        );
        if (!cancelled) setQuotePhotoUrls(dedupePhotoUrls(urls.filter(Boolean)));
      } catch {
        if (!cancelled) setQuotePhotoUrls([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [linkedQuoteId]);

  const allPhotoUrls = useMemo(
    () => dedupePhotoUrls([...(job ? collectJobPhotoUrls(job, jobIssues) : []), ...quotePhotoUrls]),
    [job, jobIssues, quotePhotoUrls],
  );

  if (!job) return <div className="p-8 text-center text-muted-foreground">Job not found.</div>;

  const jobOpps = opportunities.filter((o) => o.jobId === id);
  const hasBids = jobOpps.some((o) => o.responseStatus === "Bid Submitted" || o.responseStatus === "Accepted");
  const blockers = getJobBlockers(job, openIssues);
  const jobConflicts = id ? getJobConflicts(id) : [];
  const assignedContractor = contractors.find((c) => c.companyName === job.assignedSubcontractor);
  const assignedPerf = assignedContractor
    ? getPerformanceLabel(dispatchRec.recommendations.find(r => r.contractor.id === assignedContractor.id)?.score ?? 60)
    : null;

  const responseIcon = (status: string) => {
    switch (status) {
      case "Accepted": return <CheckCircle className="h-3.5 w-3.5 text-success" />;
      case "Rejected": return <XCircle className="h-3.5 w-3.5 text-destructive" />;
      case "Bid Submitted": return <DollarSign className="h-3.5 w-3.5 text-info" />;
      case "Viewed": return <Clock className="h-3.5 w-3.5 text-accent" />;
      case "Pending": return <Clock className="h-3.5 w-3.5 text-warning" />;
      default: return <Clock className="h-3.5 w-3.5 text-muted-foreground" />;
    }
  };

  const hasPhotos = allPhotoUrls.length > 0;
  const { milestones, pct: progressPct } = computeJobProgress(job, openIssues);
  const hasBlocker = blockers.some(b => b.severity === "critical");
  const progressLabel = getProgressLabel(progressPct, hasBlocker);
  const health = getJobHealth(job, openIssues, credits);

  return (
    <div className="max-w-[1600px] space-y-3">
      <Link to="/jobs" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Jobs
      </Link>
      <div className="flex gap-5">
        {/* ═══ LEFT COLUMN — Main Content ═══ */}
        <div className="flex-1 min-w-0 space-y-5">

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 1 — HEADER / CUSTOMER / CONTRACT VALUE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="rounded-xl border bg-card overflow-hidden shadow-sm">
        <div className="px-5 py-4 flex items-start justify-between flex-wrap gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <Link to={`/customers/${encodeURIComponent(job.customerName)}`} className="hover:text-primary transition-colors">
                <h1 className="text-xl font-bold tracking-tight">{job.customerName}</h1>
              </Link>
              <StatusBadge type="stage" value={job.currentStage} />
              {/* Only critical-signal badges here */}
              {hasBlocker && (
                <span className="inline-flex items-center gap-1 text-destructive text-[11px] font-bold bg-destructive/10 border border-destructive/20 rounded-md px-2 py-0.5">
                  <OctagonAlert className="h-3 w-3" /> BLOCKED
                </span>
              )}
              {health.status === "At Risk" && !hasBlocker && (
                <span className="inline-flex items-center gap-1 text-warning text-[11px] font-bold bg-warning/10 border border-warning/20 rounded-md px-2 py-0.5">
                  <AlertTriangle className="h-3 w-3" /> AT RISK
                </span>
              )}
              {job.issueFlag && (
                <span className="inline-flex items-center gap-1 text-destructive text-[11px] font-bold bg-destructive/10 border border-destructive/20 rounded-md px-2 py-0.5">
                  <AlertTriangle className="h-3 w-3" /> FLAGGED
                </span>
              )}
              {isInRescission(job.rescissionEndsAt) && (
                <span className="inline-flex items-center gap-1 text-warning text-[11px] font-bold bg-warning/10 border border-warning/20 rounded-md px-2 py-0.5">
                  <Shield className="h-3 w-3" /> IN RESCISSION
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground flex-wrap">
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{formatAddress(job.address)}</span>
              <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{job.phone}</span>
              {job.email && <span className="inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{job.email}</span>}
            </div>
            <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground/70 flex-wrap">
              <span className="font-mono">{job.id}</span>
              <span>·</span>
              <span>{job.branch}</span>
              <span>·</span>
              <span>{job.region}</span>
              <span>·</span>
              <span>Rep: {displayIdentity(job.salesRep, "Sales Rep")}</span>
              {linkedAppointmentResult && (
                <>
                  <span>·</span>
                  <Link
                    to={`/appointment-outcome/${linkedAppointmentResult.id}`}
                    className="text-primary hover:underline inline-flex items-center gap-1"
                  >
                    Appointment outcome
                  </Link>
                </>
              )}
              {job.marketing?.leadSource && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-0.5">Source: {job.marketing.leadSource}</span>
                </>
              )}
              {(() => {
                const src = job.marketing?.leadSource;
                const channelLabel = getGrowthChannelLabel(src);
                const channelId = src ? suggestChannel(src) : null;
                const channel = channelId ? getChannelById(channelId) : null;
                const label = channelLabel || channel?.name;
                if (!label) return null;
                return (
                  <>
                    <span>·</span>
                    <span className="flex items-center gap-0.5 text-primary font-medium">
                      <Link2 className="h-2.5 w-2.5" />
                      Acquired via: {label}
                    </span>
                  </>
                );
              })()}
              {(() => {
                const srcQuote = quotes.find(q => q.convertedJobId === job.id);
                if (!srcQuote) return null;
                return (
                  <>
                    <span>·</span>
                    <Link to={`/opportunities/${srcQuote.id}`} className="text-primary hover:underline">
                      Source: {srcQuote.id}
                    </Link>
                  </>
                );
              })()}
            </div>
          </div>
          <div className="flex flex-col items-end gap-2 shrink-0">
            {fin$.viewContractValue ? (
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.15em] font-medium">{fin$.label("Contract Value")}</p>
                <p className="text-2xl font-bold text-primary leading-tight">${getJobDisplayValue(job).toLocaleString()}</p>
              </div>
            ) : fin$.viewCrewPayout && job.financials?.subcontractorPayout ? (
              <div>
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.15em] font-medium">Install Payout</p>
                <p className="text-2xl font-bold text-primary leading-tight">${job.financials.subcontractorPayout.toLocaleString()}</p>
              </div>
            ) : null}
            <Link to={`/portal/${job.id}`} target="_blank">
              <Button variant="outline" size="sm" className="gap-1.5 text-xs">
                <ExternalLink className="h-3 w-3" /> View as Customer
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 2 — UNIFIED JOB PROGRESS / READINESS (Primary Truth)
          ═══════════════════════════════════════════════════════════════ */}
      <Card className="shadow-none border-2 border-border/60">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-sm font-bold">Job Progress</h2>
              <span className={`text-lg font-black ${progressLabel.color}`}>{progressPct}%</span>
              {blockers.length > 0 && (
                <a
                  href="#action-required"
                  onClick={(e) => {
                    e.preventDefault();
                    document.getElementById("action-required")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-destructive bg-destructive/10 border border-destructive/20 rounded-full px-2 py-0.5 hover:bg-destructive/15 transition-colors"
                  title="Jump to Action Required"
                >
                  <OctagonAlert className="h-2.5 w-2.5" />
                  {blockers.length} Blocker{blockers.length > 1 ? "s" : ""}
                </a>
              )}
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-bold ${
              hasBlocker ? "bg-destructive/10 text-destructive border-destructive/30" :
              progressPct >= 100 ? "bg-success/10 text-success border-success/30" :
              progressPct >= 50 ? "bg-warning/10 text-warning border-warning/30" :
              "bg-destructive/10 text-destructive border-destructive/30"
            }`}>
              {hasBlocker && <OctagonAlert className="h-3 w-3" />}
              {!hasBlocker && progressPct >= 100 && <CheckCircle className="h-3 w-3" />}
              {!hasBlocker && progressPct < 100 && progressPct >= 50 && <Clock className="h-3 w-3" />}
              {!hasBlocker && progressPct < 50 && <AlertTriangle className="h-3 w-3" />}
              {progressLabel.text}
            </span>
          </div>

          {/* Progress bar */}
          <div className="relative mb-4">
            <div className="h-3 rounded-full bg-muted overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${getProgressColor(progressPct, hasBlocker)}`}
                style={{ width: `${progressPct}%` }}
              />
            </div>
            {/* Milestone dots */}
            <div className="flex justify-between mt-1">
              {milestones.map((m) => (
                <div key={m.label} className="flex flex-col items-center">
                  <div className={`h-2.5 w-2.5 rounded-full border-2 -mt-[15px] relative z-10 ${
                    m.done ? "bg-success border-success" : "bg-background border-muted-foreground/30"
                  }`} />
                  <span className={`text-[9px] font-semibold mt-1 ${m.done ? "text-success" : "text-muted-foreground"}`}>{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Status Snapshot — single horizontal row, click to jump to detail */}
          {(() => {
            const snapshot = [
              {
                key: "permit",
                label: "Permit",
                status: !job.permitRequired ? "Not Required" : job.permitStatus === "Approved" ? "Approved" : job.permitStatus,
                ok: !job.permitRequired || job.permitStatus === "Approved",
                icon: Shield,
                target: "execution-permit",
              },
              {
                key: "materials",
                label: "Materials",
                status: ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness) ? "Ready" : job.materialReadiness,
                ok: ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness),
                icon: Package,
                target: "execution-materials",
              },
              {
                key: "crew",
                label: "Crew",
                status: job.assignmentType !== "Unassigned" ? displayIdentity(job.assignedCrew || job.assignedSubcontractor, "Assigned") : "Unassigned",
                ok: job.assignmentType !== "Unassigned",
                icon: Users,
                target: "execution-schedule",
              },
              {
                key: "issues",
                label: "Issues",
                status: openIssues.length === 0 ? "Clear" : `${openIssues.length} Open`,
                ok: openIssues.length === 0,
                icon: ShieldAlert,
                target: "action-required",
              },
            ];
            return (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {snapshot.map((item) => {
                  const ItemIcon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => document.getElementById(item.target)?.scrollIntoView({ behavior: "smooth", block: "start" })}
                      className={`group flex items-center gap-2 rounded-lg border px-3 py-2 text-left transition-all hover:shadow-sm hover:-translate-y-px ${
                        item.ok ? "border-success/30 bg-success/5 hover:border-success/50" : "border-warning/30 bg-warning/5 hover:border-warning/50"
                      }`}
                    >
                      {item.ok ? (
                        <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />
                      ) : (
                        <Clock className="h-3.5 w-3.5 text-warning shrink-0" />
                      )}
                      <div className="min-w-0 flex-1">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">{item.label}</p>
                        <p className={`text-[11px] font-bold truncate ${item.ok ? "text-success" : "text-warning"}`}>{item.status}</p>
                      </div>
                      {!item.ok && (
                        <ArrowRight className="h-3 w-3 text-warning opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 3 — ACTION REQUIRED TO MOVE JOB FORWARD
          (Merged: Blockers + Suggested Next Actions, deduplicated)
          ═══════════════════════════════════════════════════════════════ */}
      {(() => {
        const actions = getJobSuggestedActions(job, openIssues);
        const nextAction = getJobNextAction(job, openIssues);
        const lastAct = getLastActivity(job);

        // Map blocker categories → action icons used by suggestedActions, so we can dedupe.
        const ACTION_ICONS: Record<string, React.ElementType> = {
          crew: Users, materials: Package, permit: Shield, schedule: Calendar,
          issue: AlertTriangle, dispatch: Truck, qa: ClipboardCheck, closeout: FileCheck,
        };

        // Action keywords already covered → suppress duplicate blockers in this section
        const actionKeywords = new Set(
          actions.flatMap(a => [a.icon, a.label.toLowerCase()])
        );
        const dedupedBlockers = blockers.filter(b => {
          const lbl = b.label.toLowerCase();
          if (actionKeywords.has("permit") && lbl.includes("permit")) return false;
          if (actionKeywords.has("materials") && (lbl.includes("material") || lbl.includes("missing"))) return false;
          if (actionKeywords.has("crew") && (lbl.includes("crew") || lbl.includes("assign"))) return false;
          if (actionKeywords.has("schedule") && lbl.includes("schedul")) return false;
          if (actionKeywords.has("issue") && lbl.includes("issue")) return false;
          if (actionKeywords.has("qa") && lbl.includes("qa")) return false;
          return true;
        });

        // Apple-level cap: top 4 cards max, sorted by impact (critical blockers → high-priority actions).
        const MAX_ACTION_CARDS = 4;
        const sevRank = (s: string) => (s === "critical" ? 0 : s === "warning" ? 1 : 2);
        const prioRank = (p: string) => (p === "high" ? 0 : p === "medium" ? 1 : 2);
        const sortedBlockers = [...dedupedBlockers].sort((a, b) => sevRank(a.severity) - sevRank(b.severity));
        const sortedActions = [...actions].sort((a, b) => prioRank(a.priority) - prioRank(b.priority));
        // Next Step is hoisted into its own hero row — exclude from grid to avoid duplication.
        const remainingActions = nextAction
          ? sortedActions.filter(a => !(a.label === nextAction.label && a.icon === nextAction.icon))
          : sortedActions;
        const sortedActionsForGrid = [...remainingActions].sort((a, b) => prioRank(a.priority) - prioRank(b.priority));
        const blockerSlots = Math.min(sortedBlockers.length, MAX_ACTION_CARDS);
        const visibleBlockers = sortedBlockers.slice(0, blockerSlots);
        const visibleActions = sortedActionsForGrid.slice(0, MAX_ACTION_CARDS - blockerSlots);
        const totalItems = dedupedBlockers.length + actions.length;
        const visibleCount = visibleBlockers.length + visibleActions.length;
        const hiddenCount = Math.max(0, totalItems - visibleCount);
        const allClear = totalItems === 0;

        if (allClear) {
          return (
            <div id="action-required" className="rounded-xl border border-success/30 bg-success/[0.03] p-4 flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-success shrink-0" />
              <div>
                <p className="text-sm font-bold text-success">No actions needed</p>
                <p className="text-xs text-muted-foreground">Job is on track. All gates clear.</p>
              </div>
            </div>
          );
        }

        return (
          <Card id="action-required" className={`shadow-none border-2 ${hasBlocker ? "border-destructive/30" : "border-primary/20"}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between pb-2 mb-3 border-b">
                <div className="flex items-center gap-2">
                  {hasBlocker ? <OctagonAlert className="h-4 w-4 text-destructive" /> : <Zap className="h-4 w-4 text-primary" />}
                  <h2 className={`text-sm font-bold ${hasBlocker ? "text-destructive" : ""}`}>
                    Action Required to Move Job Forward
                  </h2>
                  <span className="text-[10px] font-semibold text-muted-foreground bg-muted/60 rounded-full px-2 py-0.5">
                    {totalItems}
                  </span>
                </div>
                {lastAct && (
                  <span className="text-[10px] text-muted-foreground hidden sm:inline">
                    Last activity: <span className="font-medium text-foreground">{formatRelativeTime(lastAct.date)}</span> by {lastAct.user}
                  </span>
                )}
              </div>

              {/* ── NEXT STEP — single highest-priority action, stronger emphasis ── */}
              {nextAction && (() => {
                const NextIcon = ACTION_ICONS[nextAction.icon] || Zap;
                const ctaLabel = nextAction.cta || nextAction.label;
                return (
                  <div className="mb-3 rounded-xl border-2 border-primary bg-primary/[0.06] px-4 py-3 flex items-start gap-3 shadow-sm">
                    <div className="p-2 rounded-lg bg-primary/15 border border-primary/30 shrink-0">
                      <NextIcon className="h-4 w-4 text-primary" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-bold uppercase tracking-[0.12em] text-primary bg-primary/10 border border-primary/30 rounded px-1.5 py-0.5">Next Step</span>
                        <p className="text-sm font-bold text-foreground truncate">{nextAction.label}</p>
                      </div>
                      <p className="text-[11px] text-muted-foreground mt-1 leading-snug">{nextAction.description}</p>
                    </div>
                    <div className="shrink-0 flex flex-col items-end gap-1">
                      <PermissionGate permission="jobs_edit">
                        <Button
                          size="sm"
                          onClick={() => handleActionClick(nextAction)}
                          className="h-8 gap-1 text-xs font-bold"
                        >
                          {ctaLabel} <ArrowRight className="h-3 w-3" />
                        </Button>
                      </PermissionGate>
                      {nextAction.link && nextAction.executionMode === "modal" && (
                        <Link to={nextAction.link} className="text-[10px] text-muted-foreground hover:text-primary hover:underline">
                          Open full view
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {/* Critical blockers without a direct action — surface first */}
                {visibleBlockers.map((b, idx) => (
                  <div
                    key={`blk-${idx}`}
                    className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${
                      b.severity === "critical" ? "border-destructive/30 bg-destructive/5" :
                      b.severity === "warning" ? "border-warning/30 bg-warning/5" :
                      "border-muted bg-muted/20"
                    }`}
                  >
                    {b.severity === "critical"
                      ? <XCircle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                      : <Clock className="h-4 w-4 text-warning mt-0.5 shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className={`text-xs font-semibold ${b.severity === "critical" ? "text-destructive" : "text-warning"}`}>
                        {b.label}
                      </p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{b.detail}</p>
                    </div>
                  </div>
                ))}

                {/* Actionable items — primary CTA verb shown on the button */}
                {visibleActions.map((action, idx) => {
                  const Icon = ACTION_ICONS[action.icon] || Zap;
                  const isHigh = action.priority === "high";
                  const ctaLabel = action.cta || action.label;
                  return (
                    <div
                      key={`act-${idx}`}
                      className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 ${
                        isHigh ? "border-primary/30 bg-primary/[0.03]" : "border-border bg-muted/20"
                      }`}
                    >
                      <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${isHigh ? "text-primary" : "text-muted-foreground"}`} />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className={`text-xs font-semibold ${isHigh ? "text-primary" : "text-foreground"}`}>{action.label}</p>
                          {isHigh && (
                            <span className="text-[8px] font-bold uppercase tracking-wider text-primary bg-primary/10 border border-primary/20 rounded px-1 py-0">High</span>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5">{action.description}</p>
                        <div className="mt-2 flex items-center gap-2">
                          <PermissionGate permission="jobs_edit">
                            <Button
                              size="sm"
                              variant={isHigh ? "default" : "outline"}
                              onClick={() => handleActionClick(action)}
                              className="h-7 gap-1 text-[11px] font-semibold px-2.5"
                            >
                              {ctaLabel} <ArrowRight className="h-2.5 w-2.5" />
                            </Button>
                          </PermissionGate>
                          {action.link && action.executionMode === "modal" && (
                            <Link to={action.link} className="text-[10px] text-muted-foreground hover:text-primary hover:underline">
                              Open full view
                            </Link>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hiddenCount > 0 && (
                <p className="mt-3 text-[10px] text-muted-foreground text-center">
                  Showing top {visibleCount} of {totalItems} · {hiddenCount} additional item{hiddenCount > 1 ? "s" : ""} below in detail panels
                </p>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* ═══ RESCISSION PERIOD BANNER ═══ */}
      {isInRescission(job.rescissionEndsAt) && (
        <Card className="shadow-none border-warning/30 bg-warning/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-warning/10 border border-warning/20">
                  <Shield className="h-5 w-5 text-warning" />
                </div>
                <div>
                  <h3 className="text-sm font-bold flex items-center gap-1.5 text-warning">
                    Right of Rescission — {getRescissionDaysRemaining(job.rescissionEndsAt)} Business Day{getRescissionDaysRemaining(job.rescissionEndsAt) !== 1 ? "s" : ""} Remaining
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5" title="Rescission period — customer's legal right to cancel">
                    Material assignments and committed orders are on hold until {formatRescissionEnd(job.rescissionEndsAt)}.
                    Stock allocations are held but not finalized.
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="text-[9px] border-warning/30 text-warning shrink-0">
                Expires {formatRescissionEnd(job.rescissionEndsAt)}
              </Badge>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Alerts panel removed — content was duplicating the unified "Action Required" surface above.
          The Job Activity right-side panel still surfaces system + alert events in chronological order. */}

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 5 — EXECUTION ZONE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="space-y-4">
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] flex items-center gap-2">
          <Wrench className="h-3.5 w-3.5" /> Execution
        </p>

        {/* Permit · Materials · Schedule — 3-column */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card id="execution-permit" className="shadow-none scroll-mt-20">
            <CardContent className="p-4">
              <SectionHeader
                icon={Shield}
                label="Permit"
                action={
                  <Link to="/permitting" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                    Tracker <ExternalLink className="h-2.5 w-2.5" />
                  </Link>
                }
              />
              <div className="divide-y">
                <InfoRow label="Required" value={job.permitRequired ? "Yes" : "No"} />
                <InfoRow label="Status" value={<StatusBadge type="permit" value={job.permitRequired ? job.permitStatus : "Not Required"} />} />
                {job.permitDetail && (
                  <>
                    <InfoRow label="Type" value={job.permitDetail.permitType} />
                    <InfoRow label="Municipality" value={job.permitDetail.municipality} />
                    {job.permitDetail.blockers && <InfoRow label="Blockers" value={<span className="text-destructive">{job.permitDetail.blockers}</span>} />}
                  </>
                )}
              </div>
              {/* Inline CTA — only when state is non-ideal */}
              {job.permitRequired && job.permitStatus !== "Approved" && (
                <Link
                  to="/permitting"
                  className="mt-3 inline-flex items-center justify-center w-full gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-md bg-warning/10 text-warning border border-warning/30 hover:bg-warning/15 transition-colors"
                >
                  {job.permitStatus === "Revision Needed" ? "Address Revision" : job.permitStatus === "Submitted" ? "Follow Up on Permit" : "Submit Permit"}
                  <ArrowRight className="h-2.5 w-2.5" />
                </Link>
              )}
            </CardContent>
          </Card>

          <Card id="execution-materials" className="shadow-none scroll-mt-20">
            <CardContent className="p-4">
              <SectionHeader
                icon={Package}
                label="Materials"
                action={
                  <Link to="/warehouse" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                    Warehouse <ExternalLink className="h-2.5 w-2.5" />
                  </Link>
                }
              />
              <div className="divide-y">
                <InfoRow label="Planning" value={<span className="text-[11px] font-semibold">{job.planningStatus || "Not Started"}</span>} />
                <InfoRow label="Readiness" value={<StatusBadge type="materialReadiness" value={job.materialReadiness} />} />
                {job.missingItemsList && (
                  <InfoRow label="Missing" value={<span className="text-destructive font-medium">{job.missingItemsList}</span>} />
                )}
                {job.materialReadinessNotes && (
                  <InfoRow label="Notes" value={<span className="text-muted-foreground text-[11px]">{job.materialReadinessNotes}</span>} />
                )}
              </div>
              {/* Two-stage Prep → Push surface (handles all "Not Ordered" states). */}
              {job.materialReadiness === "Not Ordered" && (
                <div className="mt-3">
                  <OrderPrepCard jobId={job.id} />
                </div>
              )}
              {/* Fallback CTA for post-order recovery states */}
              {["Missing Items", "Partially Received"].includes(job.materialReadiness) && (
                <Link
                  to="/warehouse"
                  className="mt-3 inline-flex items-center justify-center w-full gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-md bg-warning/10 text-warning border border-warning/30 hover:bg-warning/15 transition-colors"
                >
                  {job.materialReadiness === "Missing Items" ? "Resolve Missing Items" : "Track Delivery"}
                  <ArrowRight className="h-2.5 w-2.5" />
                </Link>
              )}
            </CardContent>
          </Card>

          <Card id="execution-schedule" className="shadow-none scroll-mt-20">
            <CardContent className="p-4">
              <SectionHeader
                icon={Truck}
                label="Schedule & Dispatch"
                action={
                  <Link to="/scheduling" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                    Calendar <ExternalLink className="h-2.5 w-2.5" />
                  </Link>
                }
              />
              <div className="divide-y">
                <InfoRow label="Install Date" value={
                  <span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3 text-muted-foreground" />{job.scheduledInstallDate || "TBD"}</span>
                } />
                <InfoRow label="Target Date" value={job.targetInstallDate || "TBD"} />
                <InfoRow
                  label="Time Slot"
                  value={formatDispatchTimeSlot(job.timeSlot, job.scheduledStartTime, job.scheduledEndTime)}
                />
                <InfoRow label="Assigned To" value={
                  job.assignmentType === "Subcontractor"
                    ? <span className="inline-flex items-center gap-1"><Users className="h-3 w-3 text-accent" />{displayIdentity(job.assignedSubcontractor, "Pending")}</span>
                    : <span className="inline-flex items-center gap-1"><Users className="h-3 w-3 text-info" />{displayIdentity(job.assignedCrew, "Unassigned")}</span>
                } />
                <InfoRow label="Dispatch" value={<StatusBadge type="dispatch" value={job.dispatchStatus} />} />
              </div>
              {/* Inline CTA — only when state is non-ideal */}
              {(!job.scheduledInstallDate || job.assignmentType === "Unassigned") && (
                <Link
                  to="/scheduling"
                  className="mt-3 inline-flex items-center justify-center w-full gap-1 px-3 py-1.5 text-[11px] font-semibold rounded-md bg-warning/10 text-warning border border-warning/30 hover:bg-warning/15 transition-colors"
                >
                  {job.assignmentType === "Unassigned" ? "Assign Crew" : "Schedule Install"}
                  <ArrowRight className="h-2.5 w-2.5" />
                </Link>
              )}

              {jobConflicts.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {jobConflicts.map((c, i) => (
                    <div key={i} className={`flex items-start gap-2 rounded-md border px-3 py-2 text-[11px] ${
                      c.type === "crew_overlap" ? "border-destructive/30 bg-destructive/5 text-destructive" : "border-warning/30 bg-warning/5 text-warning"
                    }`}>
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <div>
                        <p className="font-semibold">{c.label}</p>
                        <p className="opacity-80">{c.detail}</p>
                        {c.relatedJobIds && c.relatedJobIds.length > 0 && (
                          <div className="flex gap-1 mt-1">
                            {c.relatedJobIds.map((rid) => (
                              <Link key={rid} to={`/jobs/${rid}`} className="underline font-medium hover:opacity-70">{rid}</Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Assignment & Contractor Network — internal dispatch view; hidden from sales rep */}
        {fin$.viewDispatchInternals && (
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader
              icon={Users}
              label="Assignment & Contractor Network"
              action={
                <div className="flex gap-1.5">
                  <PermissionGate permission="jobs_edit">
                    <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => setSendDialogOpen(true)}>
                      <Send className="h-3 w-3" /> Send Opportunity
                    </Button>
                  </PermissionGate>
                  {hasBids && !job.assignedSubcontractor && (
                    <PermissionGate permission="jobs_edit">
                      <Button size="sm" className="h-7 text-[11px]" onClick={() => setSelectWinnerOpen(true)}>
                        <Trophy className="h-3 w-3" /> Select Winner
                      </Button>
                    </PermissionGate>
                  )}
                  {job.contractorAssignmentStatus === "Rejected — Reassignment Needed" && (
                    <PermissionGate permission="jobs_edit">
                      <Button size="sm" className="h-7 text-[11px] bg-warning text-warning-foreground hover:bg-warning/90" onClick={() => setSelectWinnerOpen(true)}>
                        <Users className="h-3 w-3" /> Reassign
                      </Button>
                    </PermissionGate>
                  )}
                </div>
              }
            />

            {/* Pending acceptance banner */}
            {job.dispatchStatus === "Awaiting Response" && job.assignedSubcontractor && (
              <div className="rounded-lg border-2 border-warning/30 bg-warning/5 p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="h-4 w-4 text-warning" />
                  <span className="text-xs font-bold text-warning">Pending Contractor Acceptance</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  {job.assignedSubcontractor} has been selected but hasn't accepted yet.
                </p>
              </div>
            )}

            {/* Rejection/reassignment banner */}
            {job.contractorAssignmentStatus === "Rejected — Reassignment Needed" && (
              <div className="rounded-lg border-2 border-destructive/30 bg-destructive/5 p-3 mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <XCircle className="h-4 w-4 text-destructive" />
                  <span className="text-xs font-bold text-destructive">Contractor Rejected — Reassignment Needed</span>
                </div>
                <p className="text-[11px] text-muted-foreground">
                  The assigned contractor declined this job. Select another contractor.
                </p>
                {(() => {
                  const rejectedOpp = jobOpps.find((o) => o.rejectionReason);
                  return rejectedOpp?.rejectionReason ? (
                    <p className="text-[11px] text-muted-foreground mt-1 italic">Reason: "{rejectedOpp.rejectionReason}"</p>
                  ) : null;
                })()}
              </div>
            )}

            {/* Assignment summary */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-4">
              <div className="p-2.5 rounded-lg bg-muted/30 border text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">Type</p>
                <p className="font-medium text-xs">{job.assignmentType}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30 border text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">Assigned To</p>
                {assignedContractor ? (
                  <div className="flex flex-col items-center gap-1">
                    <Link to={`/contractors/${assignedContractor.id}`} className="font-medium text-xs text-primary hover:underline">
                      {assignedContractor.companyName}
                    </Link>
                    <div className="flex items-center gap-1">
                      <ReliabilityBadge scorecard={computeScorecard(assignedContractor, opportunities, jobs, issues, credits)} />
                      {assignedPerf && (
                        <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-bold ${getPerformanceBadgeClasses(dispatchRec.recommendations.find(r => r.contractor.id === assignedContractor.id)?.score ?? 60)}`}>
                          {assignedPerf.label}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="font-medium text-xs">{displayIdentity(job.assignedCrew, "Unassigned")}</p>
                )}
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30 border text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">Target Date</p>
                <p className="font-medium text-xs">{job.targetInstallDate || "TBD"}</p>
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30 border text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">Dispatch</p>
                <StatusBadge type="dispatch" value={job.dispatchStatus} />
              </div>
              <div className="p-2.5 rounded-lg bg-muted/30 border text-center">
                <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">Acceptance</p>
                <span className={`text-xs font-semibold ${
                  job.contractorAssignmentStatus === "Accepted" ? "text-success" :
                  job.contractorAssignmentStatus?.includes("Rejected") ? "text-destructive" :
                  job.contractorAssignmentStatus === "Pending Acceptance" ? "text-warning" :
                  "text-muted-foreground"
                }`}>
                  {job.contractorAssignmentStatus || "—"}
                </span>
              </div>
            </div>

            {/* Smart Dispatch Recommendations */}
            <div className="mb-4">
              {("gated" in dispatchRec && dispatchRec.gated) && (
                <div className="rounded-lg border border-warning/30 bg-warning/5 p-3 mb-3">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-warning" />
                    <span className="text-xs font-semibold text-warning">
                      {("gateBlockers" in dispatchRec && dispatchRec.gateBlockers[0])
                        ? `Dispatch gated — clear ${dispatchRec.gateBlockers[0].label.toLowerCase()} to unlock`
                        : "Dispatch gated — assignment still available"}
                    </span>
                  </div>
                  {("gateReason" in dispatchRec && dispatchRec.gateReason) && (
                    <p className="text-[11px] text-muted-foreground mt-1">{dispatchRec.gateReason}</p>
                  )}
                </div>
              )}
              {dispatchRec.blocked && "blockReason" in dispatchRec && dispatchRec.blockReason && (
                <div className="rounded-lg border border-muted bg-muted/30 p-3 mb-3">
                  <p className="text-[11px] text-muted-foreground">{dispatchRec.blockReason}</p>
                </div>
              )}

              <div className="flex items-center justify-between mb-2">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1.5">
                  <Zap className="h-3 w-3" /> Smart Dispatch Recommendations
                </p>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-[10px]"
                  onClick={() => setShowRecommendations(!showRecommendations)}
                >
                  {showRecommendations ? "Hide" : "Show"} Top 3
                </Button>
              </div>

              {/* Compact view */}
              {!showRecommendations && dispatchRec.recommendations.length > 0 && (
                <div className="rounded-lg border bg-muted/10 p-2.5">
                  <p className="text-[11px] text-muted-foreground">
                    <span className="font-semibold text-foreground">{dispatchRec.recommendations[0].contractor.companyName}</span>
                    {" "}is the top recommendation
                    <span className="font-bold text-primary">{" "}({dispatchRec.recommendations[0].score}pts)</span>
                  </p>
                </div>
              )}

              {/* Full recommendations */}
              {showRecommendations && (
                <div className="space-y-2">
                  {dispatchRec.recommendations.slice(0, 3).map((rec, idx) => (
                    <div key={rec.contractor.id} className={`rounded-lg border p-3 ${idx === 0 ? "border-primary/20 bg-primary/[0.02]" : "bg-muted/10"}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-muted text-[10px] font-bold">#{idx + 1}</span>
                          <Link to={`/contractors/${rec.contractor.id}`} className="text-xs font-bold text-primary hover:underline">
                            {rec.contractor.companyName}
                          </Link>
                          <ReliabilityBadge scorecard={computeScorecard(rec.contractor, opportunities, jobs, issues, credits)} />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[9px] font-bold ${getPerformanceBadgeClasses(rec.score)}`}>
                            {getPerformanceLabel(rec.score)?.label}
                          </span>
                          <span className="text-xs font-bold">{rec.score}pts</span>
                        </div>
                      </div>
                      {rec.riskFlags.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {rec.riskFlags.map((r, i) => (
                            <span key={i} className="inline-flex items-center rounded bg-muted text-[9px] font-medium px-1.5 py-0.5 text-muted-foreground border">
                              {r}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  {dispatchRec.recommendations.length === 0 && (
                    <div className="text-center py-4 text-xs text-muted-foreground">No eligible contractors found</div>
                  )}
                </div>
              )}
            </div>

            {/* Opportunity history */}
            {jobOpps.length > 0 ? (
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em]">
                    Contractor Responses ({jobOpps.length})
                  </p>
                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="text-success font-medium">{jobOpps.filter((o) => o.responseStatus === "Accepted").length} accepted</span>
                    <span className="text-info font-medium">{jobOpps.filter((o) => o.responseStatus === "Bid Submitted").length} bids</span>
                    <span className="text-warning font-medium">{jobOpps.filter((o) => o.responseStatus === "Pending" || o.responseStatus === "No Response").length} pending</span>
                    <span className="text-destructive font-medium">{jobOpps.filter((o) => o.responseStatus === "Rejected").length} rejected</span>
                  </div>
                </div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-muted/40 border-b">
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Contractor</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">Sent</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden sm:table-cell">Viewed</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Response</th>
                        <th className="text-right py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider">Bid</th>
                        <th className="text-left py-2 px-3 font-semibold text-muted-foreground uppercase text-[10px] tracking-wider hidden md:table-cell">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobOpps.map((opp) => {
                        const con = contractors.find((c) => c.id === opp.contractorId);
                        const isWinner = opp.isWinner || con?.companyName === job.assignedSubcontractor;
                        return (
                          <tr key={opp.id} className={`border-b last:border-0 ${isWinner ? "bg-success/5" : ""}`}>
                            <td className="py-2 px-3 font-medium">
                              {con ? (
                                <Link to={`/contractors/${con.id}`} className="text-primary hover:underline">{con.companyName}</Link>
                              ) : (
                                <span>{opp.contractorId}</span>
                              )}
                              {isWinner && (
                                <span className="ml-1.5 inline-flex items-center text-[9px] font-bold text-success">
                                  <Trophy className="h-2.5 w-2.5 mr-0.5" /> WINNER
                                </span>
                              )}
                              {opp.rejectionReason && (
                                <p className="text-[10px] text-destructive mt-0.5 italic">Declined: {opp.rejectionReason}</p>
                              )}
                            </td>
                            <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{opp.sentDate}</td>
                            <td className="py-2 px-3 text-muted-foreground hidden sm:table-cell">{opp.viewedDate || "—"}</td>
                            <td className="py-2 px-3"><StatusBadge type="contractorResponse" value={opp.responseStatus} /></td>
                            <td className="py-2 px-3 text-right font-medium">{opp.bidAmount ? `$${opp.bidAmount.toLocaleString()}` : "—"}</td>
                            <td className="py-2 px-3 text-muted-foreground hidden md:table-cell max-w-[200px] truncate">{opp.bidNotes || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="rounded-lg border border-dashed p-6 text-center">
                <Users className="h-6 w-6 mx-auto text-muted-foreground/40 mb-2" />
                <p className="text-xs font-medium">No opportunities sent yet</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Send this job to your contractor network to start receiving bids</p>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        <SendOpportunityDialog open={sendDialogOpen} onOpenChange={setSendDialogOpen} job={job} />
        <SelectWinnerDialog open={selectWinnerOpen} onOpenChange={setSelectWinnerOpen} job={job} opportunities={jobOpps} />

        {/* Warehouse Execution — internal staging detail; hidden from Sales/CX-only roles */}
        {fin$.viewWarehouseInternals && (
          <Card className="shadow-none">
            <CardContent className="p-4">
              <SectionHeader icon={Package} label="Warehouse Execution" action={
                <Link to="/warehouse" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                  Warehouse Hub <ExternalLink className="h-2.5 w-2.5" />
                </Link>
              } />
              <JobWarehouseExecution jobId={job.id} />
            </CardContent>
          </Card>
        )}

        {/* Material Planning */}
        {/* Material Planning section removed — data shown in Warehouse Execution above */}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 6 — QUALITY & CLOSEOUT ZONE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="space-y-4">
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] flex items-center gap-2">
          <ClipboardCheck className="h-3.5 w-3.5" /> Quality & Closeout
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Install QA Status */}
          <Card className="shadow-none">
            <CardContent className="p-4">
              <SectionHeader icon={ClipboardCheck} label="Install QA" />
              <div className={`rounded-xl border-2 p-4 text-center ${
                job.qaSubmitted ? "border-success/30 bg-success/5" : "border-warning/30 bg-warning/5"
              }`}>
                {job.qaSubmitted ? (
                  <div className="flex flex-col items-center gap-1.5">
                    <CheckCircle className="h-6 w-6 text-success" />
                    <p className="text-sm font-bold text-success">QA Submitted</p>
                    <p className="text-[10px] text-muted-foreground">Full documentation on file</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-1.5">
                    <ClipboardCheck className="h-6 w-6 text-warning" />
                    <p className="text-sm font-bold text-warning">QA Required</p>
                    <p className="text-[10px] text-muted-foreground mb-2">Must be completed before closeout</p>
                    <Link
                      to="/qa-form"
                      className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-bold hover:bg-primary/90 transition-colors active:scale-[0.97]"
                    >
                      <ClipboardCheck className="h-3.5 w-3.5" /> Submit QA Form
                    </Link>
                  </div>
                )}
              </div>

              <div className="mt-4 space-y-1.5">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">Documentation Checklist</p>
                {[
                  { label: "Before photos captured", done: job.qaSubmitted },
                  { label: "Progress photos captured", done: job.qaSubmitted },
                  { label: "Completion checklist verified", done: job.qaSubmitted },
                  { label: "Final photos captured", done: job.qaSubmitted },
                  { label: "Issue documentation", done: job.qaSubmitted || !job.issueFlag },
                  { label: "Final confirmation signed", done: job.qaSubmitted },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-2">
                    {item.done ? (
                      <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />
                    ) : (
                      <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 shrink-0" />
                    )}
                    <span className={`text-[11px] ${item.done ? "text-foreground" : "text-muted-foreground"}`}>{item.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Issues & Resolutions */}
          <Card className="shadow-none">
            <CardContent className="p-4">
              <SectionHeader
                icon={ShieldAlert}
                label={`Issues (${jobIssues.length})`}
                action={
                  <Link to="/issue-form" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">
                    Report Issue <AlertTriangle className="h-3 w-3" />
                  </Link>
                }
              />

              {jobIssues.length === 0 ? (
                <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                  <CheckCircle className="h-4 w-4 mr-2 text-success" /> No issues reported
                </div>
              ) : (
                <div className="space-y-4">
                  {openIssues.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-destructive uppercase tracking-[0.12em] mb-2">Open ({openIssues.length})</p>
                      <div className="space-y-2">
                        {openIssues.map((i) => (
                          <div key={i.id} className="p-3 rounded-lg border border-destructive/20 bg-destructive/[0.03]">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-semibold text-xs">{i.issueType}</span>
                              <div className="flex gap-1.5">
                                <StatusBadge type="severity" value={i.severity} />
                                <StatusBadge type="issueStatus" value={i.status} />
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground">{i.description}</p>
                            <p className="text-[10px] text-muted-foreground mt-1.5">Owner: {i.owner} · {i.dateCreated}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {resolvedIssues.length > 0 && (
                    <div>
                      <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-2">Resolved ({resolvedIssues.length})</p>
                      <div className="space-y-2">
                        {resolvedIssues.map((i) => (
                          <div key={i.id} className="p-3 rounded-lg border bg-muted/20">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="font-medium text-xs text-muted-foreground">{i.issueType}</span>
                              <div className="flex gap-1.5">
                                <StatusBadge type="severity" value={i.severity} />
                                <StatusBadge type="issueStatus" value={i.status} />
                              </div>
                            </div>
                            <p className="text-xs text-muted-foreground">{i.description}</p>
                            <p className="text-[10px] text-muted-foreground mt-1">Owner: {i.owner} · {i.dateCreated}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

        </div>

        {/* Project Closeout */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader
              icon={FileCheck}
              label="Project Closeout"
              action={
                <Link to="/closeout" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                  Closeout Manager <ExternalLink className="h-2.5 w-2.5" />
                </Link>
              }
            />
            <div className="flex items-center gap-3 mb-3">
              <StatusBadge type="closeout" value={job.closeoutStatus} />
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-5 gap-x-4 gap-y-1.5 mb-4">
              {[
                { label: "Install complete", done: (() => { const cfg = getStageConfig(job.currentStage); return (cfg?.order ?? 0) >= 90 || job.closeoutStatus !== "Not Started"; })() },
                { label: "QA submitted & passed", done: job.qaSubmitted },
                { label: "Final documentation complete", done: job.finalDocumentationComplete },
                { label: "All issues resolved", done: openIssues.length === 0 },
                { label: "Customer signoff", done: job.customerSignoff },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-2">
                  {item.done ? (
                    <CheckCircle className="h-3.5 w-3.5 text-success shrink-0" />
                  ) : (
                    <div className="h-3.5 w-3.5 rounded-full border-2 border-muted-foreground/30 shrink-0" />
                  )}
                  <span className={`text-[11px] ${item.done ? "text-foreground" : "text-muted-foreground"}`}>{item.label}</span>
                </div>
              ))}
            </div>

            {job.closeoutNotes && (
              <div className="rounded-lg bg-muted/20 border p-3">
                <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.12em] mb-1">Closeout Notes</p>
                <p className="text-xs text-muted-foreground">{job.closeoutNotes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* QA Photo Timeline */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader
              icon={Camera}
              label="QA Photo Timeline"
              action={
                !job.qaSubmitted ? (
                  <Link to="/qa-form" className="text-[10px] text-primary hover:underline inline-flex items-center gap-1">
                    <ImagePlus className="h-3 w-3" /> Upload via QA
                  </Link>
                ) : undefined
              }
            />

            {!hasPhotos ? (
              <div className="rounded-xl border border-dashed p-5 text-center">
                <Image className="h-6 w-6 mx-auto text-muted-foreground/40 mb-1.5" />
                <p className="text-xs font-medium text-muted-foreground">No photos uploaded yet</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">Photos from QA, issues, and job modules will appear here.</p>
              </div>
            ) : (
              <div className="relative">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                  {allPhotoUrls.map((url, idx) => (
                    <a
                      key={`${url}-${idx}`}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      className="group block rounded-lg border overflow-hidden bg-muted/20"
                    >
                      <div className="aspect-[4/3] bg-muted/30">
                        <StorageThumbnailImage
                          src={url}
                          alt={`Job photo ${idx + 1}`}
                          className="w-full h-full object-cover group-hover:scale-[1.01] transition-transform"
                          onError={(e) => {
                            const target = e.currentTarget;
                            target.style.display = "none";
                          }}
                        />
                      </div>
                    </a>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="h-3.5 w-3.5 text-success" />
                    <span className="text-[10px] font-bold text-success uppercase tracking-wider">Unified Photo Feed</span>
                  </div>
                  <span className="text-[10px] text-muted-foreground">
                    {allPhotoUrls.length} photo{allPhotoUrls.length === 1 ? "" : "s"} from QA + issues + job records
                  </span>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 7 — FINANCIAL & ATTRIBUTION ZONE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="space-y-4">
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] flex items-center gap-2">
          <DollarSign className="h-3.5 w-3.5" /> Financial & Attribution
        </p>

        {/* Financial Summary */}
        {fin$.viewFinancialSummary ? (() => {
          const fin = getJobFinancialSummary(job, credits);
          const collect = getCollectionSummary(job);
          const priority = getJobPriority(job, openIssues, credits);
          const priorityColor = priority.level === "Critical"
            ? "text-destructive bg-destructive/10 border-destructive/20"
            : priority.level === "Attention Needed"
            ? "text-warning bg-warning/10 border-warning/20"
            : "text-success bg-success/10 border-success/20";

          const statusLabel: Record<string, string> = {
            no_invoice_plan: "No Invoice Plan",
            deposit_pending: "Deposit Pending",
            deposit_paid: "Deposit Paid",
            progress_pending: "Progress Pending",
            final_pending: "Final Pending",
            paid_in_full: "Paid in Full",
            overdue: "Overdue",
          };
          const statusTone =
            collect.paymentStatus === "overdue" ? "text-destructive"
            : collect.paymentStatus === "paid_in_full" ? "text-success"
            : collect.paymentStatus === "no_invoice_plan" ? "text-muted-foreground"
            : "text-warning";
          const outstandingTone = collect.outstandingBalance > 0 ? "text-destructive" : "text-success";

          // Core: contract + collection reality. No repeated values.
          const coreItems = [
            fin$.viewContractValue && { label: fin$.label("Contract"), value: `$${fin.currentContract.toLocaleString()}`, highlight: true, warn: false },
            fin$.viewContractValue && { label: "Invoiced", value: `$${collect.totalInvoiced.toLocaleString()}`, highlight: false, warn: false },
            fin$.viewContractValue && { label: "Collected", value: `$${collect.totalPaid.toLocaleString()}`, highlight: false, warn: false },
            fin$.viewContractValue && { label: "Outstanding", value: `$${collect.outstandingBalance.toLocaleString()}`, highlight: false, warn: collect.outstandingBalance > 0 },
            { label: "Status", value: statusLabel[collect.paymentStatus] ?? collect.paymentStatus, highlight: false, warn: collect.paymentStatus === "overdue", customTone: statusTone },
          ].filter(Boolean) as Array<{ label: string; value: string; highlight?: boolean; warn?: boolean; customTone?: string }>;

          // Conditional cells — only when meaningful (no dead "—" cells).
          const conditionalItems = [
            fin$.viewChangeOrderValue && fin.changeOrderCount > 0 && {
              label: fin$.label("Change Orders"),
              value: `$${fin.totalChangeOrderValue.toLocaleString()} (${fin.changeOrderCount})`,
              highlight: false, warn: false,
            },
            fin$.viewCredits && fin.creditCount > 0 && {
              label: fin$.label("Credits Issued"),
              value: `$${fin.totalCredits.toLocaleString()} (${fin.creditCount})`,
              highlight: false, warn: true,
            },
          ].filter(Boolean) as Array<{ label: string; value: string; highlight?: boolean; warn?: boolean }>;

          const summaryItems = [...coreItems, ...conditionalItems];
          // unused locals retained from prior shape:
          void outstandingTone;

          return (
            <Card className="shadow-none">
              <CardContent className="p-4">
                <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">{fin$.label("Financial Summary")}</h2>
                  </div>
                  <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold ${priorityColor}`}>
                    {priority.level}
                  </span>
                </div>
                <div className={`grid grid-cols-2 sm:grid-cols-${Math.min(summaryItems.length, 5)} gap-3`}>
                  {summaryItems.map((item) => (
                    <div key={item.label} className="p-3 rounded-lg border bg-muted/20 text-center">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">{item.label}</p>
                      <p className={`text-base font-bold mt-0.5 ${(item as { customTone?: string }).customTone ?? (item.highlight ? "text-primary" : item.warn ? "text-destructive" : "")}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
                {priority.reasons.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {priority.reasons.map((r) => (
                      <span key={r} className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[10px] font-medium ${priorityColor}`}>
                        {r}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })() : fin$.viewCrewPayout && job.financials?.subcontractorPayout ? (
          <Card className="shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 pb-1.5 mb-3 border-b">
                <DollarSign className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">Compensation</h2>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg border bg-muted/20 text-center">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Install Payout</p>
                  <p className="text-base font-bold text-primary mt-0.5">${job.financials.subcontractorPayout.toLocaleString()}</p>
                </div>
                <div className="p-3 rounded-lg border bg-muted/20 text-center">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Payment Status</p>
                  <p className={`text-base font-bold mt-0.5 ${job.financials.payoutStatus === "Paid" ? "text-success" : "text-warning"}`}>
                    {job.financials.payoutStatus || "Pending"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Contract Document */}
        {job.contractDocument && fin$.viewContractValue && (
          <ContractDocumentView contract={job.contractDocument} compact />
        )}

        {/* Change Orders */}
        {job.contractDocument?.status === "Signed" && fin$.viewChangeOrderValue && (
          <Card className="shadow-none">
            <CardContent className="p-4">
              <div className="flex items-center justify-between pb-1.5 mb-3 border-b">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <h2 className="text-xs font-bold text-muted-foreground uppercase tracking-[0.12em]">
                    Change Orders {(job.changeOrders?.length ?? 0) > 0 && `(${job.changeOrders!.length})`}
                  </h2>
                </div>
                {hasPerm("jobs_edit") && (
                  <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setChangeOrderOpen(true)}>
                    <FileText className="h-3 w-3" /> New Change Order
                  </Button>
                )}
              </div>
              {(job.changeOrders?.length ?? 0) > 0 ? (
                <ChangeOrderHistory changeOrders={job.changeOrders!} currentContractValue={getJobDisplayValue(job)} />
              ) : (
                <p className="text-xs text-muted-foreground italic">No change orders recorded.</p>
              )}
            </CardContent>
          </Card>
        )}

<ChangeOrderDialog
           open={changeOrderOpen}
           onOpenChange={setChangeOrderOpen}
           job={job}
           existingOrders={job.changeOrders ?? []}
           onSubmit={async (co: ChangeOrder) => {
             const updatedOrders = [...(job.changeOrders ?? []), co];
             const updatedJob = {
               ...job,
               changeOrders: updatedOrders,
               financials: job.financials
                 ? { ...job.financials, contractValue: co.revisedContractValue }
                 : job.financials,
               activityHistory: [
                 {
                   id: `act-${Date.now()}`,
                   date: new Date().toISOString(),
                   action: `Change Order CO-${co.orderNumber} signed — contract revised to $${co.revisedContractValue.toLocaleString()}`,
                   user: "Current User",
                   noteType: "ops" as const,
                 },
                 ...job.activityHistory,
               ],
             };
             const { data, error } = await JobService.update(job.id, updatedJob);
             if (error || !data) {
               console.error("[JobDetail] Failed to save change order:", error);
               toast.error("Failed to save change order");
               return;
             }
             setJobs(prev => prev.map(j => j.id === job.id ? data : j));
           }}
         />

        {/* Financials Detail */}
        {(fin$.viewContractValue || fin$.viewInternalCost || fin$.viewCrewPayout) && (
          <Card className="shadow-none">
            <CardContent className="p-4">
              <SectionHeader
                icon={CreditCard}
                label={fin$.isCrew ? "Compensation" : "Financials"}
                action={
                  fin$.viewFinancialSummary ? (
                    <Link to="/accounting" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                      Accounting <ExternalLink className="h-2.5 w-2.5" />
                    </Link>
                  ) : undefined
                }
              />
              {job.financials ? (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                    {[
                      fin$.viewContractValue && { label: fin$.label("Contract Value"), value: `$${job.financials.contractValue.toLocaleString()}`, color: "text-primary" },
                      fin$.viewInternalCost && { label: "Material Cost", value: `$${job.financials.estimatedMaterialCost.toLocaleString()}`, color: "text-muted-foreground" },
                      fin$.viewInternalCost && { label: "Labor Cost", value: `$${job.financials.estimatedLaborCost.toLocaleString()}`, color: "text-muted-foreground" },
                      (fin$.viewCrewPayout || fin$.viewContractValue) && { label: fin$.label("Sub Payout"), value: job.financials.subcontractorPayout > 0 ? `$${job.financials.subcontractorPayout.toLocaleString()}` : "—", color: "text-muted-foreground" },
                      fin$.viewMargin && { label: "Est. Margin", value: `$${job.financials.marginEstimate.toLocaleString()} (${job.financials.contractValue > 0 ? ((job.financials.marginEstimate / job.financials.contractValue) * 100).toFixed(0) : 0}%)`, color: "text-success" },
                    ].filter(Boolean).map((item: any) => (
                      <div key={item.label} className="p-2 rounded-lg bg-muted/30 border text-center">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">{item.label}</p>
                        <p className={`text-sm font-bold ${item.color}`}>{item.value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3">
                    {fin$.viewCustomerPaymentStatus && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground font-medium">Payment:</span>
                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${
                          job.financials.paymentStatus === "Paid" ? "bg-success/10 text-success border-success/20" :
                          job.financials.paymentStatus === "Overdue" ? "bg-destructive/10 text-destructive border-destructive/20" :
                          "bg-muted text-muted-foreground border-border"
                        }`}>{job.financials.paymentStatus}</span>
                      </div>
                    )}
                    {(fin$.viewCrewPayout || fin$.viewContractValue) && job.financials.subcontractorPayout > 0 && (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground font-medium">{fin$.isCrew ? "Status:" : "Payout:"}</span>
                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium border ${
                          job.financials.payoutStatus === "Paid" ? "bg-success/10 text-success border-success/20" :
                          "bg-muted text-muted-foreground border-border"
                        }`}>{job.financials.payoutStatus}</span>
                      </div>
                    )}
                  </div>
                </>
              ) : fin$.isCrew ? (
                <p className="text-xs text-muted-foreground italic">Payout information not yet available.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {[
                    fin$.viewContractValue && "Contract Value",
                    fin$.viewInternalCost && "Material Cost",
                    fin$.viewInternalCost && "Labor Cost",
                    "Sub Payout",
                    fin$.viewMargin && "Est. Margin",
                  ].filter(Boolean).map((label) => (
                    <div key={label as string} className="p-2 rounded-lg bg-muted/20 border border-dashed text-center">
                      <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold mb-0.5">{label}</p>
                      <p className="text-sm font-bold text-muted-foreground/40">—</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Job Economics */}
        {fin$.viewFinancialSummary && (
          <Card className="shadow-none">
            <CardContent className="p-4">
              <JobEconomicsBreakdown job={job} />
            </CardContent>
          </Card>
        )}

        {/* Detailed Financials Panel */}
        {fin$.viewFinancialSummary && (
          <Card className="shadow-none">
            <CardContent className="p-4">
              <SectionHeader icon={DollarSign} label="Financials" />
              <JobFinancialsPanel
                job={job}
                onJobUpdate={(updated) => setJobs(prev => prev.map(j => j.id === updated.id ? updated : j))}
                canManage={fin$.isFullAccess}
              />
            </CardContent>
          </Card>
        )}

        {/* Customer Resolutions & Credits */}
        {fin$.viewCredits && (() => {
          const jobCredits = credits.filter((c) => c.jobId === id);
          const totalCredited = jobCredits.reduce((s, c) => s + c.creditAmount, 0);
          const jobIssuesForCredit = issues.filter((i) => i.jobId === id);
          return (
            <Card className="shadow-none">
              <CardContent className="p-4">
                <SectionHeader
                  icon={CreditCard}
                  label="Customer Resolutions & Credits"
                  action={
                    !showJobCreditForm ? (
                      <PermissionGate permission="jobs_edit">
                        <Button size="sm" variant="outline" className="text-xs gap-1 h-6" onClick={() => setShowJobCreditForm(true)}>
                          <CreditCard className="h-3 w-3" /> Add Credit
                        </Button>
                      </PermissionGate>
                    ) : undefined
                  }
                />
                {jobCredits.length === 0 && !showJobCreditForm ? (
                  <div className="flex items-center justify-center py-6 text-xs text-muted-foreground">
                    <CheckCircle className="h-4 w-4 mr-2 text-success" /> No credits or resolutions on this job
                  </div>
                ) : jobCredits.length > 0 && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                      <div className="rounded-lg border p-2.5 bg-muted/20 text-center">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Total Credits</p>
                        <p className="text-sm font-bold text-primary">${totalCredited.toLocaleString()}</p>
                      </div>
                      <div className="rounded-lg border p-2.5 bg-muted/20 text-center">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Records</p>
                        <p className="text-sm font-bold">{jobCredits.length}</p>
                      </div>
                      <div className="rounded-lg border p-2.5 bg-muted/20 text-center">
                        <p className="text-[9px] text-muted-foreground uppercase tracking-[0.12em] font-semibold">Linked Issues</p>
                        <p className="text-sm font-bold">{jobCredits.filter((c) => c.issueId).length}</p>
                      </div>
                    </div>
                    <div className="space-y-2 mb-3">
                      {jobCredits.map((cr) => (
                        <div key={cr.id} className="rounded-lg border p-3 bg-muted/10">
                          <div className="flex items-center justify-between mb-1.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[10px] text-muted-foreground">{cr.id}</span>
                              <span className="text-xs font-semibold">{cr.creditType}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-primary">${cr.creditAmount.toLocaleString()}</span>
                              <StatusBadge type="creditStatus" value={cr.status} />
                            </div>
                          </div>
                          <p className="text-[11px] text-muted-foreground">{cr.notes}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-[10px] text-muted-foreground">
                            <span>Category: {cr.reasonCategory}</span>
                            <span>·</span>
                            <span>Attribution: {cr.attributionType}</span>
                            <span>·</span>
                            <span>{cr.dateCreated}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
                {showJobCreditForm && (
                  <div className="mt-3">
                    <CreateCreditForm
                      jobId={job.id}
                      customerId={job.customerName}
                      availableIssues={jobIssuesForCredit}
                      onSubmit={(credit: CustomerCredit) => {
                        setCredits(prev => [...prev, credit]);
                        setShowJobCreditForm(false);
                      }}
                      onCancel={() => setShowJobCreditForm(false)}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}

        {/* Marketing Attribution */}
        {job.marketing && (
          <Card className="shadow-none">
            <CardContent className="p-4">
               <SectionHeader
                 icon={Megaphone}
                 label="Marketing Attribution"
                 action={
                   <div className="flex items-center gap-2">
                     <Link to="/marketing-attribution" className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                       Performance <ExternalLink className="h-2.5 w-2.5" />
                     </Link>
                     <PermissionGate permission="jobs_edit">
                       <Button
                         variant="ghost"
                         size="sm"
                         className="h-7 px-2 text-[10px]"
                         onClick={() => setEditMarketingOpen(true)}
                       >
                         <Pencil className="h-3 w-3 mr-1" />
                         Edit
                       </Button>
                     </PermissionGate>
                   </div>
                 }
               />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                <InfoRow label="Lead Source" value={job.marketing.leadSource} />
                <InfoRow label="Campaign" value={job.marketing.campaignName} />
                {job.marketing.aggregatorPlatform && <InfoRow label="Platform" value={job.marketing.aggregatorPlatform} />}
                <InfoRow label="Call Center Rep" value={job.marketing.callCenterRep || "—"} />
                <InfoRow label="Appointment" value={job.marketing.appointmentDate} />
                <InfoRow label="Outcome" value={job.marketing.saleOutcome} />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Version History */}
        {(() => {
          const lineage = buildLineageFromRecords(
            job.convertedFromQuoteId || job.id,
            job.projectConfig,
            getJobDisplayValue(job),
            job.contractDocument,
            job.changeOrders,
          );
          if (lineage.versions.length <= 1) return null;
          return (
            <Card className="shadow-none">
              <CardContent className="p-4">
                <VersionComparisonBanner lineage={lineage} />
                <div className="mt-3">
                  <VersionTimeline lineage={lineage} />
                </div>
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 8 — TIMELINE / AUDIT / SUPPORTING DETAIL
          ═══════════════════════════════════════════════════════════════ */}
      <div className="space-y-4">
        <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-[0.15em] flex items-center gap-2">
          <Clock className="h-3.5 w-3.5" /> Timeline & Supporting Detail
        </p>

        {/* Record Health (collapsed context) */}
        <RecordHealthPanel
          title="Job Health"
          blockers={unifiedBlockers}
          nextSteps={jobNextSteps}
          completeness={jobCompleteness}
          onCompletenessClick={(label) => {
            if (label === "Issues Resolved") setIssuesModalOpen(true);
          }}
        />

        {/* Project Overview */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader icon={User} label="Project Overview" />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="divide-y">
                <InfoRow label="Project Type" value={job.projectType} />
                <InfoRow label="Scope" value={job.scopeSummary} />
              </div>
              <div className="divide-y">
                <InfoRow label="Branch" value={job.branch} />
                <InfoRow label="Region" value={job.region} />
              </div>
              <div className="divide-y">
                <InfoRow label="Sales Rep" value={displayIdentity(job.salesRep, "Sales Rep")} />
                <InfoRow label="Source" value={job.marketing?.leadSource || "—"} />
              </div>
              <div className="divide-y">
                <InfoRow label="Assignment" value={job.assignmentType} />
                <InfoRow label="Crew/Sub" value={displayIdentity(job.assignedCrew || job.assignedSubcontractor, "Unassigned")} />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* JobSpec — execution source of truth */}
        <JobSpecViewer bound={job.boundJobSpec} />

        {/* JobSpec — Spec Reconciliation Gate (PM gate after Demo qa_passed) */}
        <SpecReconciliationGate jobId={job.id} bound={job.boundJobSpec} />

        {/* Materials / BOM — read-only canonical view (Spec source + PO/Kit overlays) */}
        <JobBomPanel jobId={job.id} bound={job.boundJobSpec} job={job} />

        {/* JobSpec — Material Kit (gates install phase) */}
        <MaterialKitPanel jobId={job.id} bound={job.boundJobSpec} currentStage={job.currentStage} />

        {/* JobSpec — installer execution (gated by photos + checklist) */}
        <JobSpecExecutionPanel jobId={job.id} bound={job.boundJobSpec} />

        {/* JobSpec — QA review (PASS/FAIL per phase) */}
        <JobSpecQAPanel jobId={job.id} bound={job.boundJobSpec} />

        {/* JobSpec — outcome record (read-only, appears once all phases QA-pass) */}
        <SpecOutcomeCard jobId={job.id} bound={job.boundJobSpec} />

        {/* Lifecycle Tracker (secondary) */}
        <Card className="shadow-none">
          <CardContent className="p-3">
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em] mb-2">Lifecycle Progress</p>
            <LifecycleTracker steps={(() => {
              const displayStages = getOrderedStages(true)
                .filter(s => s.id !== "Issue")
                .filter(s => !["Review Complete", "Ready for Ordering", "Permit and Order Pending", "Scheduled Tentative", "Ready for Dispatch", "Available for Contractor Assignment", "Assigned and Dispatched"].includes(s.id));
              const currentConfig = getStageConfig(job.currentStage);
              const currentOrder = currentConfig?.order ?? 0;
              return displayStages.map((s) => ({
                label: s.label,
                completed: s.order < currentOrder || job.currentStage === "Completed",
                active: s.id === job.currentStage || (currentConfig && s.order === currentOrder && job.currentStage !== "Completed"),
              }));
            })()} />
          </CardContent>
        </Card>

        {/* Job Lifecycle Timeline */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader icon={Clock} label="Job Lifecycle Timeline" />
            {(() => {
              const lifecycle: { date: string; label: string; detail: string; icon: React.ElementType; color: string; done: boolean }[] = [];
              const createdEntry = job.activityHistory.find((a) => a.action.toLowerCase().includes("created") || a.action.toLowerCase().includes("sale closed"));
              lifecycle.push({ date: createdEntry?.date || job.activityHistory[job.activityHistory.length - 1]?.date || "", label: "Job Created", detail: createdEntry ? `${createdEntry.user}` : "System", icon: FileText, color: "text-primary", done: true });
              const schedEntry = job.activityHistory.find((a) => a.action.toLowerCase().includes("schedul"));
              const isScheduled = !!job.scheduledInstallDate || !!schedEntry;
              lifecycle.push({ date: schedEntry?.date || job.scheduledInstallDate || "", label: "Scheduled", detail: isScheduled ? `Install: ${job.scheduledInstallDate}${job.timeSlot ? ` · ${job.timeSlot}` : ""}` : "Not yet scheduled", icon: Calendar, color: "text-primary", done: isScheduled });
              const matsReady = ["Fully Received", "Staged for Job", "Loaded for Crew"].includes(job.materialReadiness);
              const matsEntry = job.activityHistory.find((a) => a.action.toLowerCase().includes("material"));
              lifecycle.push({ date: matsEntry?.date || "", label: "Materials Ready", detail: `Status: ${job.materialReadiness}`, icon: Package, color: "text-success", done: matsReady });
              const dispatched = ["Assigned", "Closed"].includes(job.dispatchStatus);
              const dispEntry = job.activityHistory.find((a) => a.action.toLowerCase().includes("dispatch") || a.action.toLowerCase().includes("assigned"));
              lifecycle.push({ date: dispEntry?.date || "", label: "Dispatched", detail: dispatched ? `Crew: ${displayIdentity(job.assignedCrew || job.assignedSubcontractor, "—")}` : `Status: ${job.dispatchStatus}`, icon: Truck, color: "text-warning", done: dispatched });
              const stageOrder = getStageConfig(job.currentStage)?.order ?? 0;
              const inProgress = stageOrder >= (getStageConfig("In Progress")?.order ?? 80);
              const ipEntry = job.activityHistory.find((a) => a.action.toLowerCase().includes("progress") || a.action.toLowerCase().includes("started"));
              lifecycle.push({ date: ipEntry?.date || "", label: "In Progress", detail: inProgress ? "Installation underway" : "Not started", icon: Wrench, color: "text-warning", done: inProgress });
              lifecycle.push({ date: job.qaSubmitted ? job.activityHistory.find(a => a.action.toLowerCase().includes("qa"))?.date || "" : "", label: "QA Complete", detail: job.qaSubmitted ? "Quality assurance passed" : "Pending", icon: ClipboardCheck, color: "text-success", done: job.qaSubmitted });
              const isClosed = (getStageConfig(job.currentStage)?.order ?? 0) >= 100;
              const closeEntry = job.activityHistory.find((a) => a.action.toLowerCase().includes("completed") || a.action.toLowerCase().includes("closed"));
              lifecycle.push({ date: closeEntry?.date || "", label: "Job Closed", detail: isClosed ? "Completed" : job.closeoutStatus !== "Not Started" ? `Closeout: ${job.closeoutStatus}` : "In progress", icon: CheckCircle, color: "text-success", done: isClosed });
              const doneCount = lifecycle.filter((e) => e.done).length;
              const progress = Math.round((doneCount / lifecycle.length) * 100);
              return (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 mb-1">
                    <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                      <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${progress}%` }} />
                    </div>
                    <span className="text-xs font-bold text-foreground whitespace-nowrap">{doneCount}/{lifecycle.length} complete</span>
                  </div>
                  <div className="border rounded-lg bg-muted/10 overflow-hidden">
                    {lifecycle.map((event, idx) => {
                      const EvIcon = event.icon;
                      return (
                        <div key={idx} className={`flex gap-3 px-4 py-3 ${idx < lifecycle.length - 1 ? "border-b" : ""} ${event.done ? "bg-background" : "bg-muted/20"}`}>
                          <div className="flex flex-col items-center pt-0.5">
                            <div className={`w-7 h-7 rounded-full flex items-center justify-center border shrink-0 ${event.done ? `${event.color} bg-background border-current/20` : "text-muted-foreground/40 bg-muted border-border"}`}>
                              {event.done ? <EvIcon className="h-3.5 w-3.5" /> : <Circle className="h-3 w-3" />}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className={`text-sm font-medium ${event.done ? "text-foreground" : "text-muted-foreground"}`}>{event.label}</p>
                              {event.done && <CheckCircle className="h-3 w-3 text-success shrink-0" />}
                            </div>
                            <p className="text-xs text-muted-foreground mt-0.5">{event.detail}</p>
                          </div>
                          {event.date && <span className="text-[10px] text-muted-foreground whitespace-nowrap pt-1 shrink-0">{event.date}</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* Job Summary */}
        <Card className="shadow-none">
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Zap className="h-3.5 w-3.5 text-primary shrink-0" />
              <p className="text-xs font-medium text-foreground">{getJobSummary(job)}</p>
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader icon={FileText} label="Documents" />
            {(() => {
              const docs = buildJobDocuments(job, quotes as any, issues);
              return <DocumentCenter documents={docs} compact />;
            })()}
            {buildJobDocuments(job, quotes as any, issues).length === 0 && (
              <p className="text-xs text-muted-foreground italic">No documents on record.</p>
            )}
          </CardContent>
        </Card>

        {/* Audit Trail */}
        <Card className="shadow-none">
          <CardContent className="p-4">
            <SectionHeader icon={Clock} label="Audit Trail" />
            {(() => {
              const auditEntries = buildJobAuditTrail(job, quotes as any, issues, credits);
              return auditEntries.length > 0
                ? <AuditTrailTimeline entries={auditEntries} />
                : <ActivityTimeline entries={job.activityHistory} />;
            })()}
          </CardContent>
        </Card>
      </div>
      </div>
    </div>

      {/* ═══ UNRESOLVED ISSUES MODAL ═══ */}
      <Dialog open={issuesModalOpen} onOpenChange={setIssuesModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-destructive" />
              Unresolved Issues ({openIssues.length})
            </DialogTitle>
          </DialogHeader>
          {openIssues.length === 0 ? (
            <div className="flex flex-col items-center py-8 text-muted-foreground">
              <CheckCircle className="h-8 w-8 text-success mb-2" />
              <p className="text-sm font-medium">All issues resolved</p>
            </div>
          ) : (
            <div className="space-y-3">
              {openIssues.map((issue) => (
                <div key={issue.id} className={`rounded-lg border p-4 ${
                  issue.severity === "Urgent" ? "border-destructive/30 bg-destructive/5" : "border-warning/30 bg-warning/5"
                }`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      {issue.severity === "Urgent" ? (
                        <XCircle className="h-4 w-4 text-destructive shrink-0" />
                      ) : (
                        <AlertTriangle className="h-4 w-4 text-warning shrink-0" />
                      )}
                      <span className="text-sm font-semibold">{issue.issueType}</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <StatusBadge type="severity" value={issue.severity} />
                      <StatusBadge type="issueStatus" value={issue.status} />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">{issue.description}</p>
                  <div className="grid grid-cols-2 gap-2 text-[10px] text-muted-foreground">
                    <span><strong>Owner:</strong> {issue.owner}</span>
                    <span><strong>Reported:</strong> {issue.dateCreated}</span>
                    <span><strong>Source:</strong> {issue.source || "Internal"}</span>
                    <span><strong>Job:</strong> {issue.jobId}</span>
                  </div>
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t">
                    <Link
                      to={`/issues?selected=${issue.id}`}
                      className="text-[10px] text-primary hover:underline font-medium inline-flex items-center gap-1"
                      onClick={() => setIssuesModalOpen(false)}
                    >
                      View Full Details <ArrowRight className="h-2.5 w-2.5" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Modal-first quick actions — each renders inline PushNextStepPanel.
            onChooseNextStep handles chain continuity (close current, open next
            in same render — no toast, no perceptible gap). ── */}
      {orderingOpen && (
        <OrderingModal
          open={orderingOpen}
          onOpenChange={setOrderingOpen}
          job={job}
          openIssues={openIssues}
          onSendToOrdering={handleOrderingSubmit}
          onChooseNextStep={(step) => {
            setOrderingOpen(false);
            if (step.kind === "assign_crew") setAssignOpen(true);
            else if (step.kind === "reschedule") setRescheduleOpen(true);
            else if (step.kind === "navigate") navigate(step.to);
            // generate_po / view_permit / resolve_issue / done → just close
          }}
        />
      )}
      <RescheduleModal
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        job={job}
        openIssues={openIssues}
        onChooseNextStep={(step) => {
          setRescheduleOpen(false);
          if (step.kind === "assign_crew") setAssignOpen(true);
          else if (step.kind === "generate_po") setOrderingOpen(true);
          else if (step.kind === "navigate") navigate(step.to);
        }}
      />
      <QuickAssignModal
        open={assignOpen}
        onOpenChange={setAssignOpen}
        job={job}
        openIssues={openIssues}
        onChooseNextStep={(step) => {
          setAssignOpen(false);
          if (step.kind === "reschedule") setRescheduleOpen(true);
          else if (step.kind === "generate_po") setOrderingOpen(true);
          else if (step.kind === "navigate") navigate(step.to);
        }}
      />
      <QuickResolveIssueModal
        open={!!resolveIssueTarget}
        onOpenChange={(open) => { if (!open) setResolveIssueTarget(null); }}
        issue={resolveIssueTarget}
        onChooseNextStep={(step) => {
          setResolveIssueTarget(null);
          if (step.kind === "assign_crew") setAssignOpen(true);
          else if (step.kind === "reschedule") setRescheduleOpen(true);
          else if (step.kind === "generate_po") setOrderingOpen(true);
          else if (step.kind === "navigate") navigate(step.to);
        }}
      />
      <EditMarketingModal
        open={editMarketingOpen}
        onOpenChange={setEditMarketingOpen}
        job={job!}
        onSaved={handleMarketingSaved}
      />
    </div>
  );
}
